/**
 * Wizard hook: when an org is created, offer to create the first
 * application in it. This is the only `org.created` hook today —
 * future backup / monitoring / etc. plugins register their own at
 * higher `order` values (see types.ts for the convention).
 *
 * Implementation note: this is a thin wrapper around the existing
 * CreateApplicationDialog. The dialog already manages its own multi-
 * step UX internally (app form → AddComponentForm loop) — the
 * wrapper just adapts the WizardHookStepProps callbacks to the
 * dialog's open/close model.
 *
 * Reaching this state means the operator clicked "Continue setup"
 * on the org-creation success view (CreateOrgDialog). When the
 * dialog closes — whether they created an app or hit Cancel — we
 * call onComplete so the chain advances.
 */
import { useEffect, useState } from 'react';
import { CreateApplicationDialog } from '@/components/applications/CreateApplicationDialog.js';
import { useQueryClient } from '@tanstack/react-query';
import type { WizardHook, WizardHookStepProps } from '../types.js';

function ApplicationAfterOrgStep({
  isActive,
  event,
  onComplete,
}: WizardHookStepProps<'org.created'>): JSX.Element {
  const qc = useQueryClient();
  // Local open state mirrors `isActive`. Lets us hold the dialog
  // closed when the chain isn't on us, and propagate the user's
  // "X" / Cancel to onComplete.
  const [open, setOpen] = useState(isActive);

  useEffect(() => {
    setOpen(isActive);
  }, [isActive]);

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    // The dialog closed — whether by Cancel, by Done after creating
    // an app, or any path — advance the chain. We don't care
    // whether an app was actually created here; the dialog handled
    // that work and (when applicable) invalidated query caches.
    if (!next && isActive) {
      onComplete();
    }
  }

  return (
    <CreateApplicationDialog
      open={open}
      onOpenChange={handleOpenChange}
      initialOrgId={event.org.id}
      onCreated={() => {
        // Refresh the apps list — the parent page's own query
        // invalidation may not reach here when the chain is
        // sourced from a different page (Orgs.tsx triggers chains
        // for Application that lives elsewhere).
        void qc.invalidateQueries({ queryKey: ['applications'] });
      }}
    />
  );
}

export const applicationAfterOrgHook: WizardHook<'org.created'> = {
  id: 'applications.create-app-after-org',
  eventKind: 'org.created',
  // 10 leaves room for earlier-firing things (5 — "verify org"?) and
  // keeps the rest of the chain at 20+ for backup / monitoring etc.
  order: 10,
  label: 'Create first application',
  render: ApplicationAfterOrgStep,
};
